// adapted from http://openlayers.org/en/v3.6.0/examples/measure.html?q=draw

import _ = require("underscore");
import MapView = require("../views/MapView");
import ol = require("ol");
import Utils = require("../utils");
import Portal = require("portal-gis");
import Mediator = require("../mediator");
import Symbology = require("../utils/symbology");
import Base = require("./radio-command"); // adds support for 'radio-group' attribute

const toolId = "measure-tool";
const layerId = toolId + "-layer";
const wgs84Sphere = new ol.Sphere(6378137);

class MeasureTool extends Base<{}> {

    public static about = `Uses a draw interaction as a measurement tool (should be in the control namespace)`

    public static options = Utils.defaults({
        "clear": {
            about: "Clear prior measurement",
            default: "false"
        },
        "event": {
            about: "event(s) to fire upon completion",
            default: "measurement"
        },
        "geodesic": {
            "about": "perform geodesic measurements",
            "domain": "true|false",
            default: "true",
        },
        "symbology": {
            "about": "Symbology to use when rendering measurements",
            "default": "measure-tool"
        },
        "type": {
            about: "",
            "domain": "area",
            default: "area"
        },
    }, Base.options);

    static output = {
        "feature": {
            "about": "the feature representing the measurement",
        },
        "final": {
            "about": "true when this is the full measurment (tool is no longer active)",
        },
        "measure": {
            "about": "the measurement",
        },
    }
    
    private map: ol.Map;
    private symbology: string;
    private measureType: string;
    private geodesic: boolean;
    private drawTools = <Rhythm.GIS.Dictionary<ol.interaction.Draw>>{};

    go() {
        let options = Utils.defaults(this.asOptions(MeasureTool.options), {
            symbology: this.configuration.id,
            maxres: 50
        });

        this.symbology = options.symbology;
        this.geodesic = options.geodesic !== "false";

        this.measureType = options.type;

        this.mediator.mapViewMap(map => {
            this.map = map;
            this.forceSource();

            if (options["clear"] === "true") {
                this.clear();
                return;
            }
            !this.active() && this.start();
        });

    }

    stop() {
        // remove tooltips
        var draw = this.drawTools[this.measureType];
        draw && this.map.removeInteraction(draw);
    }

    private forceDrawTool(measureType: string) {
        var tool = this.drawTools[measureType];
        if (!tool) {
            let map = this.map;
            let vector = this.forceSource();
            let drawType = (measureType == "area" ? "Polygon" : "LineString");

            let feature = <Rhythm.GIS.FeatureQueryResultFeature>{
                geometryType: measureType,
                attributes: {
                    type: measureType,
                    dimensions: measureType == 'area' ? "2" : "1"
                }
            };
            Utils.extend(feature, {
                symbology: this.symbology
            });

            let style = Symbology.findStyle(this.mediator, feature);

            tool = new ol.interaction.Draw({
                source: vector.getSource(),
                style: style,
                type: drawType // Point, LineString, Polygon, Circle
            });

            tool.on("drawstart", evt => {
                console.log("start measuring");
                let sketchFeature = <ol.Feature>evt.feature;
                let cloneStyle = Symbology.findStyle(this.mediator, feature);
                sketchFeature.setStyle(cloneStyle);
                let emitMeasurement = () => {
                    let measurement = this.format(sketchFeature);
                    console.log("measurement:", measurement);

                    this.trigger({
                        measurement: measurement,
                        feature: sketchFeature,
                        final: false
                    });
                };
                let h = this.map.on("pointermove", _.throttle(emitMeasurement, 1000, { leading: false }));
                tool.once("drawend", evt => {
                    this.map.unByKey(h);
                    let measurement = this.format(sketchFeature);

                    this.trigger({
                        measurement: measurement,
                        feature: sketchFeature,
                        final: true
                    });
                });
            });

            this.drawTools[measureType] = tool;
        }
        return tool;
    }

    private forceSource() {
        var map = this.map;
        var vector = <ol.layer.Vector>map.getLayers().getArray().filter(l => l.get("id") === layerId)[0];
        var source = vector && vector.getSource();

        if (!source) {
            source = new ol.source.Vector();

            // get style from symbology
            let feature = <Rhythm.GIS.FeatureQueryResultFeature>{
                attributes: {
                    dimensions: "2",
                    symbology: this.symbology
                }
            };
            let style = Symbology.findStyle(this.mediator, feature);

            var vector = new ol.layer.Vector({
                id: layerId,
                source: source,
                style: (feature, resolution) => {
                    // TODO: should add logic to adjust font size, offset, visibility,etc.
                    return [style];
                }
            });

            map.addLayer(vector);
        }

        return vector;
    }

    private format(sketch) {
        let result = {
            value: 0,
            dimensions: 0,
            units: "m"
        };

        if (sketch) {
            let geom = (sketch.getGeometry());
            if (geom instanceof ol.geom.Polygon) {
                result.dimensions = 2;
                result.value = this.formatArea(geom);
            } else if (geom instanceof ol.geom.LineString) {
                result.dimensions = 1;
                result.value = this.formatLength(geom);
            }
        }

        return result;
    }

    private formatArea(polygon) {
        let area: number;
        if (this.geodesic) {
            var sourceProj = this.map.getView().getProjection();
            var geom = /** @type {ol.geom.Polygon} */(polygon.clone().transform(
                sourceProj, 'EPSG:4326'));
            var coordinates = geom.getLinearRing(0).getCoordinates();
            area = Math.abs(wgs84Sphere["geodesicArea"](coordinates));
        } else {
            area = polygon.getArea();
        }
        return area;
    }

    private formatLength(line) {
        let length: number;
        if (this.geodesic) {
            var coordinates = line.getCoordinates();
            length = 0;
            var sourceProj = this.map.getView().getProjection();
            for (var i = 0, ii = coordinates.length - 1; i < ii; ++i) {
                var c1 = ol.proj.transform(coordinates[i], sourceProj, 'EPSG:4326');
                var c2 = ol.proj.transform(coordinates[i + 1], sourceProj, 'EPSG:4326');
                length += wgs84Sphere.haversineDistance(c1, c2);
            }
        } else {
            length = Math.round(line.getLength() * 100) / 100;
        }
        return length;
    }

    private get type() {
        return this.measureType; // readonly
    }

    private active() {
        var result = -1 < this.map.getInteractions().getArray().indexOf(this.forceDrawTool(this.measureType));
        return result;
    }

    private start() {
        this.map.addInteraction(this.forceDrawTool(this.measureType));
    }

    private clear() {
        this.forceSource().getSource().clear();
    }

}

export = MeasureTool; 